/**
 * Legacy Call Logs — v1 `/sessions` list, detail and participants.
 * Callers pass arguments positionally, so the order is fixed.
 */
import axios from '../axiosInstance'
import constants from '../../constants'
import {
  LegacyCallsApp,
  LegacyApiErrorBody,
  LegacyRequestFailure,
  LegacyCallSessionsResult,
  LegacyCallParticipantsResult,
} from './types'

/**
 * v1 is served from a region metrics host, not the mgmt API wrapper, so
 * getChatApiBase does not apply here.
 */
function buildBase(metricsHost: string, type: string): string {
  return `${constants.isLiveEnv ? 'https://' : 'http://'}${metricsHost}/v1/${type}`
}

/** Auth is the app's stats token, which is separate from the user session bearer. */
function buildHeaders(app: LegacyCallsApp): Record<string, string> {
  return {
    appId: String(app.id),
    Authorization: `Bearer ${app.statsToken}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
}

/** Only the API's own JSON error shape has a string `error.message`. */
function isApiErrorBody(body: unknown): body is LegacyApiErrorBody {
  const candidate = body as LegacyApiErrorBody
  return typeof candidate?.error?.message === 'string'
}

// Resolves in-band so callers branch on `isLegacyFailure` rather than catch.
function toFailure(error: {
  response?: { status?: number; data?: unknown }
}): LegacyApiErrorBody | LegacyRequestFailure {
  const body = error?.response?.data
  if (isApiErrorBody(body)) return body
  return { requestFailed: true, status: error?.response?.status, body }
}

/** True for either failure shape — callers must check this before reading `data`. */
export function isLegacyFailure(
  result: unknown,
): result is LegacyApiErrorBody | LegacyRequestFailure {
  return (result as LegacyRequestFailure)?.requestFailed === true || isApiErrorBody(result)
}

/** Timestamps are epoch MILLISECONDS. Omitting them returns the app's entire history. */
export async function get(
  app: LegacyCallsApp,
  metricsHost: string,
  type = 'calls',
  startRange?: number,
  endRange?: number,
): Promise<LegacyCallSessionsResult> {
  const params = new URLSearchParams()
  if (startRange) params.append('beginningTimestamp', String(startRange))
  if (endRange) params.append('endingTimestamp', String(endRange))
  params.append('withRecordings', '1')

  const URL = `${buildBase(metricsHost, type)}/sessions?${params.toString()}`
  return axios
    .get(`${URL}`, { headers: buildHeaders(app) })
    .then(res => res.data)
    .catch(toFailure)
}

/**
 * `data` is an array even for one session id, and the timestamp range is the only
 * discriminator — one id returned 992 records. Always pass the record's own range.
 */
export async function getByID(
  app: LegacyCallsApp,
  metricsHost: string,
  sessionID: string,
  beginningTimestamp?: number,
  endingTimestamp?: number,
): Promise<LegacyCallSessionsResult> {
  const params = new URLSearchParams()
  if (beginningTimestamp) params.append('beginningTimestamp', String(beginningTimestamp))
  if (endingTimestamp) params.append('endingTimestamp', String(endingTimestamp))

  const query = params.toString()
  let URL = `${buildBase(metricsHost, 'calls')}/sessions/${sessionID}`
  if (query) URL += `?${query}`

  return axios
    .get(`${URL}`, { headers: buildHeaders(app) })
    .then(res => res.data)
    .catch(toFailure)
}

/** A range wider than the session's own makes this endpoint return an nginx 502. */
export async function getParticipants(
  app: LegacyCallsApp,
  metricsHost: string,
  type = 'calls',
  sessionID: string,
  beginningTimestamp?: number,
  endingTimestamp?: number,
): Promise<LegacyCallParticipantsResult> {
  const params = new URLSearchParams()
  if (beginningTimestamp) params.append('beginningTimestamp', String(beginningTimestamp))
  if (endingTimestamp) params.append('endingTimestamp', String(endingTimestamp))

  const query = params.toString()
  let URL = `${buildBase(metricsHost, type)}/sessions/${sessionID}/participants`
  if (query) URL += `?${query}`

  return axios
    .get(`${URL}`, { headers: buildHeaders(app) })
    .then(res => res.data)
    .catch(toFailure)
}
